/**
 * CPF e CNPJ pelo dígito verificador (#546). Contar 11 ou 14 dígitos deixava
 * passar qualquer número digitado errado.
 *
 * O CNPJ alfanumérico, emitido desde julho de 2026, também vale: as 12
 * primeiras posições aceitam letras, os dois verificadores continuam
 * numéricos, e cada caractere conta pelo código ASCII menos 48.
 */

/** Tira pontos, barra, traço e espaços; letras ficam maiúsculas. */
export const normalizeTaxId = (raw: string): string => raw.toUpperCase().replace(/[^0-9A-Z]/g, '');

const allSame = (value: string) => /^(.)\1*$/.test(value);

const cpfDigit = (base: string): number => {
  let sum = 0;
  for (let i = 0; i < base.length; i++) sum += Number(base[i]) * (base.length + 1 - i);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

export const isValidCpf = (value: string): boolean => {
  if (!/^\d{11}$/.test(value) || allSame(value)) return false;
  return (
    cpfDigit(value.slice(0, 9)) === Number(value[9]) &&
    cpfDigit(value.slice(0, 10)) === Number(value[10])
  );
};

const cnpjDigit = (base: string): number => {
  let sum = 0;
  let weight = 2;
  for (let i = base.length - 1; i >= 0; i--) {
    sum += (base.charCodeAt(i) - 48) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

export const isValidCnpj = (value: string): boolean => {
  if (!/^[0-9A-Z]{12}\d{2}$/.test(value) || allSame(value)) return false;
  return (
    cnpjDigit(value.slice(0, 12)) === Number(value[12]) &&
    cnpjDigit(value.slice(0, 13)) === Number(value[13])
  );
};

/** CPF ou CNPJ já normalizado por `normalizeTaxId`. */
export const isValidTaxId = (value: string): boolean =>
  value.length === 11 ? isValidCpf(value) : isValidCnpj(value);
